import { NavLink, useLocation, type Location } from 'react-router-dom'
import {
  LayoutDashboard, Home, Users, GitBranch, Settings, X, Briefcase,
  MessageSquare, GitPullRequest, Calendar, BarChart2, ChevronDown, Images,
  FileText, Newspaper, FileSpreadsheet, ShieldAlert, UsersRound, Globe, CheckSquare,
  Building2, Radar, Target, UserPlus, Scale, Mail, Inbox, Send, Trash2, Archive, Star,
} from 'lucide-react'
import { useAuth } from '@/contexts/AuthContext'
import { api } from '@/lib/api'
import { emailApi } from '@/lib/email-api'
import { useState, useEffect, useCallback, Fragment } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useTenantInfo } from '@/hooks/api/tenant'
import { isStaffRole, MANAGER_PLUS_ROLES, OWNER_PLUS_ROLES, STAFF_ROLES, type UserRole, personName } from '@repo/types'

interface NavItem {
  to: string
  icon: React.ElementType
  label: string
  roles?: readonly string[]
  activeWhen?: (location: Location) => boolean
  /** Key into the dynamic badge map (e.g. 'emailUnread') — renders an unread count. */
  badgeKey?: string
}

interface NavGroup {
  label: string
  icon: React.ElementType
  items: NavItem[]
  /** Badge shown on the (collapsed) group header — same map as NavItem.badgeKey. */
  badgeKey?: string
}

type SidebarEntry =
  | { kind: 'item'; item: NavItem }
  | { kind: 'group'; group: NavGroup; roles?: readonly string[] }

const MY_WEB_GROUP: NavGroup = {
  label: 'Môj web',
  icon: Globe,
  items: [
    { to: '/my-web', icon: Globe, label: 'Prehľad' },
    { to: '/my-web/settings', icon: Settings, label: 'Nastavenia' },
    { to: '/my-web/content', icon: FileText, label: 'Obsah' },
    { to: '/my-web/blog', icon: Newspaper, label: 'Blog' },
    { to: '/my-web/references', icon: MessageSquare, label: 'Referencie' },
  ],
}

const USERS_ROLES = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'ASSISTANT']
const SETTINGS_ROLES = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'ASSISTANT']
const BROKER_PLUS = ['SUPER_ADMIN', 'OWNER', 'BRANCH_MANAGER', 'BROKER']
const TIPER_ONLY: UserRole[] = ['TIPER']
const LAWYER_ONLY: UserRole[] = ['LAWYER']

const EMAILS_GROUP: NavGroup = {
  label: 'Emaily',
  icon: Mail,
  badgeKey: 'emailUnread',
  items: [
    { to: '/emails', icon: Inbox, label: 'Prijaté', badgeKey: 'emailUnread' },
    { to: '/emails/starred', icon: Star, label: 'S hviezdičkou' },
    { to: '/emails/sent', icon: Send, label: 'Odoslané' },
    { to: '/emails/drafts', icon: FileText, label: 'Koncepty' },
    { to: '/emails/archive', icon: Archive, label: 'Archív' },
    { to: '/emails/spam', icon: ShieldAlert, label: 'Spam' },
    { to: '/emails/trash', icon: Trash2, label: 'Kôš' },
  ],
}

const LUDIA_GROUP: NavGroup = {
  label: 'Ľudia',
  icon: UsersRound,
  items: [
    { to: '/crm/contacts', icon: Target, label: 'Kontakty', roles: STAFF_ROLES },
    { to: '/tipers', icon: Users, label: 'Tiperi', roles: STAFF_ROLES },
    { to: '/my-team', icon: UsersRound, label: 'Môj tím', roles: MANAGER_PLUS_ROLES },
    { to: '/users', icon: Users, label: 'Správa používateľov', roles: USERS_ROLES },
  ],
}

const SIDEBAR_SECTIONS: SidebarEntry[][] = [
  // TIPER variant — 3 items
  [
    { kind: 'item', item: { to: '/dashboard', icon: LayoutDashboard, label: 'Prehľad', roles: TIPER_ONLY } },
    { kind: 'item', item: { to: '/my-deals', icon: Briefcase, label: 'Moje obchody', roles: TIPER_ONLY } },
    { kind: 'item', item: { to: '/crm/contacts', icon: UserPlus, label: 'Kontakty', roles: TIPER_ONLY } },
  ],
  // LAWYER variant — 2 items
  [
    { kind: 'item', item: { to: '/dashboard', icon: LayoutDashboard, label: 'Prehľad', roles: LAWYER_ONLY } },
    { kind: 'item', item: { to: '/legal-requests', icon: Scale, label: 'Právne služby', roles: LAWYER_ONLY } },
  ],
  // Normal staff variants below — gated by STAFF_ROLES so TIPER/LAWYER never see them
  [
    { kind: 'item', item: { to: '/dashboard', icon: LayoutDashboard, label: 'Prehľad', roles: STAFF_ROLES } },
    { kind: 'item', item: { to: '/properties', icon: Home, label: 'Nehnuteľnosti', roles: STAFF_ROLES } },
  ],
  [
    {
      kind: 'item',
      item: {
        to: '/my-deals',
        icon: Briefcase,
        label: 'Moje obchody',
        roles: STAFF_ROLES,
        activeWhen: (loc) =>
          loc.pathname.startsWith('/crm/deals/') && (loc.state as { from?: string } | null)?.from === 'my-deals',
      },
    },
    {
      kind: 'item',
      item: {
        to: '/crm/pipeline',
        icon: GitPullRequest,
        label: 'Pipeline',
        roles: STAFF_ROLES,
        activeWhen: (loc) =>
          loc.pathname.startsWith('/crm/deals/') && (loc.state as { from?: string } | null)?.from !== 'my-deals',
      },
    },
    { kind: 'group', group: EMAILS_GROUP, roles: STAFF_ROLES },
  ],
  [
    { kind: 'group', group: LUDIA_GROUP, roles: STAFF_ROLES },
    { kind: 'item', item: { to: '/reports', icon: BarChart2, label: 'Reporty', roles: MANAGER_PLUS_ROLES } },
    { kind: 'item', item: { to: '/monitor', icon: Radar, label: 'Reality Monitor', roles: BROKER_PLUS } },
    { kind: 'item', item: { to: '/legal-requests', icon: Scale, label: 'Právne služby', roles: STAFF_ROLES } },
  ],
  [
    { kind: 'item', item: { to: '/calendar', icon: Calendar, label: 'Kalendár', roles: STAFF_ROLES } },
    { kind: 'item', item: { to: '/tasks', icon: CheckSquare, label: 'Úlohy', roles: STAFF_ROLES } },
  ],
  [
    { kind: 'item', item: { to: '/branches', icon: GitBranch, label: 'Pobočky', roles: MANAGER_PLUS_ROLES } },
    { kind: 'group', group: MY_WEB_GROUP, roles: STAFF_ROLES },
  ],
  [
    { kind: 'item', item: { to: '/exports', icon: FileSpreadsheet, label: 'Import a export', roles: OWNER_PLUS_ROLES } },
    { kind: 'item', item: { to: '/media', icon: Images, label: 'Knižnica médií', roles: STAFF_ROLES } },
    { kind: 'item', item: { to: '/settings', icon: Settings, label: 'Nastavenia', roles: SETTINGS_ROLES } },
  ],
]

/** Small unread-count pill, right-aligned, used in nav group headers + items. */
function NavBadge({ count, active }: { count: number; active?: boolean }) {
  if (!count) return null
  return (
    <span
      className={`ml-auto min-w-[18px] text-center text-[11px] font-semibold leading-none rounded-full px-1.5 py-0.5 ${
        active ? 'bg-white/25 text-white' : 'bg-brand-500 text-white'
      }`}
    >
      {count > 99 ? '99+' : count}
    </span>
  )
}

// Only items the current user is allowed to see (group items can carry per-item roles)
function visibleGroupItems(group: NavGroup, userRole: string) {
  return group.items.filter(item => !item.roles || item.roles.includes(userRole))
}

function NavGroupSection({
  group,
  userRole,
  open,
  onToggle,
  onClose,
  badges,
}: {
  group: NavGroup
  userRole: string
  open: boolean
  onToggle: () => void
  onClose: () => void
  badges: Record<string, number>
}) {
  const location = useLocation()
  const Icon = group.icon
  const visibleItems = visibleGroupItems(group, userRole)
  const isGroupActive = visibleItems.some(item => location.pathname.startsWith(item.to))

  return (
    <div>
      <button
        onClick={onToggle}
        aria-expanded={open}
        className={`flex items-center justify-between w-full px-3 py-2.5 transition-colors rounded-lg ${
          isGroupActive && !open
            ? 'bg-brand-700 text-white'
            : isGroupActive
              ? 'text-white hover:bg-brand-800'
              : 'text-brand-300 hover:bg-brand-800 hover:text-white'
        }`}
      >
        <div className="flex items-center gap-3">
          <Icon size={16} />
          <span className="text-sm font-medium">{group.label}</span>
        </div>
        <div className="flex items-center gap-1.5">
          {group.badgeKey && !open && <NavBadge count={badges[group.badgeKey] ?? 0} active={isGroupActive} />}
          <ChevronDown size={13} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {/* Rows 1fr→0fr animate the collapse; the transition is set only while closed so opening stays instant.
          `invisible` flips at the end of the transition and keeps hidden links out of tab order. */}
      <div
        className={`grid ${
          open ? 'grid-rows-[1fr]' : 'grid-rows-[0fr] invisible transition-[grid-template-rows,visibility] duration-500 ease-in-out'
        }`}
      >
        <div className="min-h-0 overflow-hidden">
          <div className="mt-0.5 ml-5 pl-2 border-l border-brand-800 space-y-0.5">
            {visibleItems.map(({ to, icon: ItemIcon, label, badgeKey }) => (
              <NavLink
                key={to}
                to={to}
                end
                onClick={onClose}
                className={({ isActive }) =>
                  `flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-700 text-white'
                      : 'text-brand-300 hover:bg-brand-800 hover:text-white'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <ItemIcon size={15} />
                    <span className="truncate">{label}</span>
                    {badgeKey && <NavBadge count={badges[badgeKey] ?? 0} active={isActive} />}
                  </>
                )}
              </NavLink>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function FlatNavLink({ item, onClose }: { item: NavItem; onClose: () => void }) {
  const Icon = item.icon
  const location = useLocation()
  const customActive = item.activeWhen?.(location) ?? false
  return (
    <NavLink
      to={item.to}
      onClick={onClose}
      className={({ isActive }) =>
        `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
          isActive || customActive
            ? 'bg-brand-700 text-white'
            : 'text-brand-300 hover:bg-brand-800 hover:text-white'
        }`
      }
    >
      <Icon size={16} />
      {item.label}
    </NavLink>
  )
}

interface SidebarProps {
  onClose: () => void
}

export function Sidebar({ onClose }: SidebarProps) {
  const { user } = useAuth()
  const queryClient = useQueryClient()

  const { data: tenant } = useTenantInfo({ enabled: user?.role !== 'SUPER_ADMIN' })

  // SUPER_ADMIN tenant selector
  const [selectedTenantId, setSelectedTenantId] = useState<string>(
    () => localStorage.getItem('superadmin_tenant_id') ?? '',
  )

  const { data: tenants } = useQuery({
    queryKey: ['super-admin-tenants'],
    queryFn: async () => {
      const { data } = await api.get('/super-admin/tenants')
      return data as Array<{ id: string; name: string; slug: string; isActive: boolean }>
    },
    enabled: user?.role === 'SUPER_ADMIN',
  })

  const handleTenantChange = useCallback((tenantId: string) => {
    setSelectedTenantId(tenantId)
    if (tenantId) {
      localStorage.setItem('superadmin_tenant_id', tenantId)
    } else {
      localStorage.removeItem('superadmin_tenant_id')
    }
    // Invalidate all queries to refetch with new tenant context
    queryClient.invalidateQueries()
  }, [queryClient])

  // Auto-select first tenant if none selected
  useEffect(() => {
    if (user?.role === 'SUPER_ADMIN' && tenants?.length && !selectedTenantId) {
      const firstActive = tenants.find(t => t.isActive)
      if (firstActive) {
        handleTenantChange(firstActive.id)
      }
    }
  }, [user?.role, tenants, selectedTenantId, handleTenantChange])

  const selectedTenantName = tenants?.find(t => t.id === selectedTenantId)?.name

  const sidebarTitle = user?.role === 'SUPER_ADMIN'
    ? (selectedTenantName ?? 'Administrácia')
    : tenant?.name ?? 'Načítavam...'

  const userRole = user?.role ?? ''
  const canShow = (roles?: readonly string[]) => !roles || roles.includes(userRole)

  // Accordion: at most one group open at a time; the group owning the current route opens on navigation
  const location = useLocation()
  const activeGroup = SIDEBAR_SECTIONS.flat()
    .flatMap(entry => (entry.kind === 'group' && canShow(entry.roles) ? [entry.group] : []))
    .find(group => visibleGroupItems(group, userRole).some(item => location.pathname.startsWith(item.to)))
    ?.label ?? null
  const [openGroup, setOpenGroup] = useState<string | null>(activeGroup)
  useEffect(() => {
    if (activeGroup) setOpenGroup(activeGroup)
  }, [activeGroup])

  // Unread INBOX count for the "Emaily" group badge. Only fetched for staff who
  // actually see the emails section; refetched periodically + on cache invalidation.
  const showEmail = isStaffRole(userRole)
  const { data: emailUnread } = useQuery({
    queryKey: ['email-unread-counts'],
    queryFn: () => emailApi.unreadCounts(),
    enabled: showEmail,
    refetchInterval: 60_000,
  })
  const badges: Record<string, number> = { emailUnread: emailUnread?.total ?? 0 }

  // Build the rendered sections, skipping sections where every entry is hidden
  const renderedSections = SIDEBAR_SECTIONS.map((section, sectionIdx) => {
    const visibleEntries = section.filter(entry => {
      if (entry.kind === 'item') return canShow(entry.item.roles)
      return canShow(entry.roles)
    })
    if (visibleEntries.length === 0) return null
    return (
      <Fragment key={sectionIdx}>
        {sectionIdx > 0 && <div className="my-2 border-t border-brand-800" />}
        {visibleEntries.map(entry => {
          if (entry.kind === 'item') {
            return <FlatNavLink key={entry.item.to} item={entry.item} onClose={onClose} />
          }
          const { label } = entry.group
          return (
            <NavGroupSection
              key={label}
              group={entry.group}
              userRole={userRole}
              open={openGroup === label}
              onToggle={() => setOpenGroup(openGroup === label ? null : label)}
              onClose={onClose}
              badges={badges}
            />
          )
        })}
      </Fragment>
    )
  }).filter(Boolean)

  return (
    <div className="flex h-full flex-col text-white" style={{ background: 'linear-gradient(to right, #08334d, #0c4a6e)' }}>
      {/* Logo */}
      <div className="flex items-center justify-between px-5 py-4 border-b border-brand-800">
        <div className="flex items-center gap-2.5">
          <div className="w-12 h-12 overflow-hidden flex items-center justify-center">
            <img src="/logo-letter-transparent.png" alt="Realnexa" className="w-full h-full object-contain scale-[1.7]" />
          </div>
          <span className="font-semibold text-sm truncate max-w-[160px]">{sidebarTitle}</span>
        </div>
        <button onClick={onClose} className="lg:hidden text-brand-300 hover:text-white">
          <X size={18} />
        </button>
      </div>

      {/* SUPER_ADMIN tenant selector */}
      {user?.role === 'SUPER_ADMIN' && tenants && (
        <div className="px-3 py-3 border-b border-brand-800">
          <label className="flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-yellow-400/70 mb-1.5 px-1">
            <Building2 size={12} />
            Pohľad tenanta
          </label>
          <select
            value={selectedTenantId}
            onChange={(e) => handleTenantChange(e.target.value)}
            className="w-full rounded-lg bg-brand-900/60 border border-brand-700 text-sm text-white px-2.5 py-2 focus:outline-none focus:ring-1 focus:ring-yellow-400/50 focus:border-yellow-400/50 appearance-none cursor-pointer"
            style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23facc15' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'right 8px center',
            }}
          >
            <option value="" className="bg-gray-800">-- Žiadny tenant --</option>
            {tenants.map(t => (
              <option key={t.id} value={t.id} className="bg-gray-800">
                {t.name} {!t.isActive ? '(neaktívny)' : ''}
              </option>
            ))}
          </select>
          {selectedTenantName && (
            <p className="text-[10px] text-yellow-400/50 mt-1 px-1 truncate">
              Zobrazujem dáta: {selectedTenantName}
            </p>
          )}
        </div>
      )}

      {/* Nav */}
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto scrollbar-hidden">
        {renderedSections}

        {/* Super Admin — only visible to SUPER_ADMIN role */}
        {user?.role === 'SUPER_ADMIN' && (
          <>
            <div className="my-2 border-t border-brand-800" />
            <NavLink
              to="/super-admin"
              onClick={onClose}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  isActive
                    ? 'bg-yellow-600/30 text-yellow-300'
                    : 'text-yellow-400/80 hover:bg-yellow-600/20 hover:text-yellow-300'
                }`
              }
            >
              <ShieldAlert size={16} />
              Správa tenantov
            </NavLink>
          </>
        )}
      </nav>

      {/* User */}
      {user && (
        <div className="px-4 py-4 border-t border-brand-800">
          <NavLink
            to="/profile"
            onClick={onClose}
            className="flex items-center gap-3 rounded-lg px-1 py-1 -mx-1 transition-colors hover:bg-brand-800"
          >
            <div className="w-8 h-8 rounded-full bg-brand-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
              {user.firstName[0]}{user.lastName[0]}
            </div>
            <div className="min-w-0">
              <p className="text-xs font-medium text-white truncate">{personName(user)}</p>
              <p className="text-xs text-brand-400 truncate">{user.email}</p>
            </div>
          </NavLink>
        </div>
      )}
    </div>
  )
}
